import React from 'react';
import * as dateUtils from '../../date.utils';
import { iterationKey, repeatIfDaily } from './utils';
import { getStudyYearForDate, getWeek, WeekYear } from '../../date.utils';
import { useFormatMessageFor } from '../../i18n/translation';
import translations from './calendar.i18n';

interface Props {
  weekNumbers: WeekYear[];
  highlightedWeek: WeekYear | null;
  isWeekly: boolean;
  startYear: number;
}

const Timeline = React.memo<Props>(
  ({ weekNumbers, highlightedWeek, isWeekly, startYear }) => {
    const labels = useFormatMessageFor(translations);

    const now = new Date(Date.now());
    const isCurrentYear =
      getStudyYearForDate(now)[0].getFullYear() === startYear;

    const currentWeek = isCurrentYear && getWeek(now);
    const currentDaySundayFirst = now.getDay();
    const currentDay = currentDaySundayFirst > 0 ? currentDaySundayFirst : 7;

    const drawerToggleCell = (rowSpan: number) => (
      <th rowSpan={rowSpan} className="first-header">
        {labels.timelineheaderActivities}
      </th>
    );

    const isLastWeekOfSemester = (weekYear: WeekYear) =>
      dateUtils.getEndWeekOfSemester(weekYear.semester, weekYear.year) ===
      weekYear.week;

    const groupedByYearAndSemester = weekNumbers.reduce(
      (acc: { [key: string]: WeekYear[] }, v: WeekYear) => {
        const key = `${v.year}|${v.semester}`;
        (acc[key] = acc[key] || []).push(v);
        return acc;
      },
      {},
    );
    const semesters = Object.keys(groupedByYearAndSemester).map((key) => ({
      sem: Number(key[5]),
      weeks: groupedByYearAndSemester[key],
    }));

    const semesterName = (semester: number) => {
      if (semester === 1) {
        return labels.timelineHeaderSpring;
      }
      return labels.timelineHeaderFall;
    };

    const renderWeekly = () => {
      return (
        <table className="timeline weeks">
          <thead>
            <tr className="semesters">
              {drawerToggleCell(2)}
              {semesters.map((it) => (
                <th
                  key={iterationKey(it.weeks[0], it.sem)}
                  colSpan={it.weeks.length}
                >
                  {semesterName(it.sem)}
                </th>
              ))}
            </tr>
            <tr className="week-numbers">
              {weekNumbers.map((it) => (
                <th
                  key={iterationKey(it)}
                  className={isLastWeekOfSemester(it) ? 'last' : ''}
                >
                  <span className={'current-week-wrapper'}>
                    <div
                      className={`circle-wrapper ${
                        it.week === (highlightedWeek ? highlightedWeek.week : 0)
                          ? 'hovered'
                          : ''
                      }`}
                    >
                      {it.week}
                    </div>
                    {it.week === currentWeek && (
                      <div className={'current-week-underline'} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
        </table>
      );
    };

    const renderDaily = () => {
      return (
        <table className="timeline days">
          <thead>
            <tr className="semesters">
              {drawerToggleCell(3)}
              {semesters.map((it) => (
                <th
                  key={iterationKey(it.weeks[0], it.sem)}
                  colSpan={it.weeks.length * 7}
                >
                  {semesterName(it.sem)}
                </th>
              ))}
            </tr>
            <tr className="week-numbers">
              {weekNumbers.map((it: WeekYear) => (
                <th
                  key={iterationKey(it)}
                  colSpan={7}
                  className={isLastWeekOfSemester(it) ? 'last' : ''}
                >
                  <span className={'current-week-wrapper'}>
                    <div
                      className={`circle-wrapper ${
                        it.week === (highlightedWeek ? highlightedWeek.week : 0)
                          ? 'hovered'
                          : ''
                      }`}
                    >
                      {it.week}
                    </div>
                    {it.week === currentWeek && (
                      <div className={'current-week-underline'} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
            <tr className="weekdays-bullets">
              {weekNumbers.map((it: WeekYear) =>
                repeatIfDaily(isWeekly, (iDay) => (
                  <th
                    key={iterationKey(it, iDay)}
                    className={
                      isLastWeekOfSemester(it) && iDay === 6 ? 'last' : ''
                    }
                  >
                    &#x25cf;
                    <span className={'current-day-wrapper'}>
                      {iDay === currentDay && it.week === currentWeek && (
                        <div className={'current-day-underline'} />
                      )}
                    </span>
                  </th>
                )),
              )}
            </tr>
          </thead>
        </table>
      );
    };

    return (
      <div className="calendar-width-limiter timeline-row">
        {isWeekly ? renderWeekly() : renderDaily()}
      </div>
    );
  },
);

export default Timeline;
